import { Modal } from '../../../../components/Modal/Modal'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Collapse, Container, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { SelectDropdown, SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { useGetAdditionalObjectsQuery, useGetIntegrationUsersQuery, useSaveMeetingMutation } from '../../../../store/integrations/integrations'
import { useLazyGetRoomSessionQuery, useListAiResultsQuery } from '../../../../store/media/media'

import { escapeHtml, formatDateHM } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../../common/errorMessages'
import { AiFeaturePreset } from '../../../../models/AiFeaturePreset'
import { RoomSession } from '../../../../models/Room'
import { MeetingTemplate } from '../../../../store/ai/meetingTemplate'
import { SourceIcon } from '../../../../apps/professional-app/fa/PastMeetings/SourceIcon'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { WritableLexicalEditor } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { useGetPresetsQuery } from '../../../../store/ai/ai'
import { getOrderedMeetingTemplatePresets, getPresetResultsByInputParameterHierarchy } from '../../../../apps/professional-app/fa/MeetingSummary/utils'
import { $getRoot, $insertNodes, EditorState, LexicalEditor } from 'lexical'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { LEXICAL_NODES } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { selectSessionMeetingTemplate } from '../../../../store/meetingSummary/meetingTemplates'
import { selectAiFeatures } from '../../../../store/meetingSummary/aiFeatures'
import { RichTextToolbarPlugin } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { copyResult } from '../../../../apps/professional-app/fa/MeetingSummary/copy'
import { useGetInputParametersHierarchyNodeByPresetsQuery } from '../../../../store/ai/inputParametersHierarchy'
import { getContactOptions } from '../utils'
import { selectMeetingInfoHTML, setRoomSession } from '../../../../store/meetingSummary/roomSession'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig, OperationType } from '../../../../models/Account'
import { getEditorTextContent } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { User } from '../../../../models/User'
import { useCookies } from 'react-cookie'
import { useExportConfirmation } from '../../../../components/ConfirmationDialog/useExportConfirmation'
import { selectGroupedTasks } from '../../../../store/task/task'
import { UserSelectDropdown } from '../UserDropdown'
import { TeamSelectDropdown } from '../TeamDropdown'
import { useSyncStatuses } from '../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/hooks'
import { IntegrationSavedModalBody } from '../../../../components/Integrations/IntegrationSavedModalBody/IntegrationSavedModalBody'
import { parseMetadata } from '../../../../common/util'
import { useEffectiveSource } from '../../../../common/useEffectiveSource'
import { addToast } from '../../../../store/toast/toastSlice'
import {
  defaultIntegrationContactObject,
  defaultIntegrationMeetingExportObject,
  eventConfigMap,
  IntegrationServiceId,
  MappingConfig,
} from '../../../../common/integrations'
import { useGetRoomQuery } from '../../../../apps/professional-app/fa/store/fa'
import { ContactDropdown } from '../ContactDropdown'
import { HTML_TYPE } from '../../../../common/copy'
import { type UpdateAdditionalInformation } from '../../../../models/Account'

interface PreSelectedPresetEnvMapping {
  prod: string[]
  staging: string[]
  dev: string[]
}

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]
const DEFAUlT_ACCOUNT_CONFIG_MAPPING_SESSION = 'note'
const PRESELECTED_PRESETS: PreSelectedPresetEnvMapping = {
  dev: ['eiaibj6g6uuxmfbu', '3q77qfpmo76ekpp9', '9xbrgp80b4ls6qbx'],
  staging: ['eudwatduwtujes7r', '37wou7icth9r3ymd', '9xbrgp80b4ls6qbx'],
  prod: ['2tspyef3f59unqyl', 'dshudbybhopw2w7h', 'cqkkd1xbz240b6b7'],
}
const env = import.meta.env.REACT_APP_ENV! as 'prod' | 'staging' | 'dev'

interface CRMSessionObjectMapping {
  [key: string]: string
}

const CRM_SESSION_OBJECT: CRMSessionObjectMapping = {
  wealthbox: 'events',
  redtail: 'activities',
  salesforce: 'Event',
  practifi: 'Event',
  xlr8: 'Event',
  zoho: 'Events',
  elements: 'Event',
  blackdiamond: 'Event',
}

type NestedRecord = Record<string, unknown> & {
  [key: string]: NestedRecord | unknown[] | unknown
}

const extractEvents = (data: NestedRecord, config: MappingConfig): { id: string; label: string }[] => {
  const contactCollection = data?.[config.path]
  let contactEvents: { id: string; label: string }[] = []
  if (Array.isArray(contactCollection)) {
    contactEvents = contactCollection.map((item: Record<string, unknown>) => ({
      id: `${item[config.idKey]}`,
      label: `${item[config.labelKey]}`,
    }))
  }

  let householdEvents: { id: string; label: string }[] = []
  if (config.household) {
    const householdData = data as Record<string, unknown>
    const householdContainer = householdData[config.household.householdPath]

    if (householdContainer && typeof householdContainer === 'object') {
      const householdObj = householdContainer as Record<string, unknown>
      const householdCollection = householdObj[config.household.eventPath]

      if (Array.isArray(householdCollection)) {
        householdEvents = householdCollection.map((item: Record<string, unknown>) => ({
          id: `${item[config.idKey]}`,
          label: `${item[config.labelKey]}`,
        }))
      }
    }
  }
  return [...contactEvents, ...householdEvents].filter((event, index, self) => self.findIndex((e) => e.id === event.id) === index)
}

export interface AddMeetingToCRMModalProps {
  roomSession: RoomSession
  meetingTemplate?: MeetingTemplate
  show: boolean
  onHide: () => void
  serviceId: IntegrationServiceId
  body?: React.ReactNode
}

type FormValues = {
  externalUserId: string
  meetingDescription: string
  externalMeetingId: string | null
  notifyUserId: string | null
  notifyTeamId: string | null
}

const EVENT_DATE_OFFSET = 7 * 24 * 60 * 60 * 1000

export const AddMeetingToCRMModal: React.FC<AddMeetingToCRMModalProps> = ({ show, onHide, roomSession, meetingTemplate, serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation({ roomSession })
  const { tracked } = useTracking()
  const [triggerSaveMeeting, { isLoading }] = useSaveMeetingMutation()
  const meetingInfoHTML = useAppSelector(selectMeetingInfoHTML)
  const { data: account } = useGetAccountQuery({ accountId })
  const [triggerGetRoomSession, { isLoading: isGetRoomSessionLoading }] = useLazyGetRoomSessionQuery()
  const [savedMeetingResult, setSavedMeetingResult] = useState<boolean>(false)
  const { roomSessionSyncStatus } = useSyncStatuses()

  const serviceIntegration = useMemo(() => {
    return getAccountCRMIntegrationConfig(account?.data, serviceId)
  }, [account, serviceId])

  const sessionMapping = useMemo(() => {
    if (!serviceIntegration) {
      return DEFAUlT_ACCOUNT_CONFIG_MAPPING_SESSION
    }
    return typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
      ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
      : (serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.objectName ?? DEFAUlT_ACCOUNT_CONFIG_MAPPING_SESSION)
  }, [serviceIntegration])

  const contactObject = useMemo(() => {
    if (!serviceIntegration) {
      return defaultIntegrationContactObject[serviceId] || 'Contact'
    }
    return typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT] === 'string'
      ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT]
      : (serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT]?.objectName ?? (defaultIntegrationContactObject[serviceId] || 'Contact'))
  }, [serviceIntegration, serviceId])

  const sessionLabel = useMemo(() => {
    if (!serviceIntegration || !sessionMapping) {
      return DEFAUlT_ACCOUNT_CONFIG_MAPPING_SESSION
    }
    if (
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string' ||
      !serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.displayName
    ) {
      return sessionMapping.charAt(0).toUpperCase() + sessionMapping.slice(1)
    }

    return serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].displayName
  }, [sessionMapping, serviceIntegration])

  const isEventAppendSupported = useMemo(() => {
    if (!serviceIntegration) {
      return false
    }
    const updatable =
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
        ? null
        : Object.keys(serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.additionalInformation || {}).includes('updatable')
          ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.additionalInformation?.updatable
          : serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.operationType
            ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.operationType === OperationType.UPDATE
            : null

    return updatable === true || (updatable === null && sessionMapping === CRM_SESSION_OBJECT[serviceId])
  }, [serviceIntegration, serviceId, sessionMapping])

  const isNotifySupported = useMemo(() => {
    if (!serviceIntegration) {
      return false
    }
    return sessionMapping === defaultIntegrationMeetingExportObject[IntegrationServiceId.REDTAIL]?.id && serviceId === IntegrationServiceId.REDTAIL
  }, [serviceIntegration, sessionMapping, serviceId])

  const defaultValues = {
    externalUserId: '',
    meetingDescription: '',
    externalMeetingId: '',
  }

  const updateInformation = useMemo(() => {
    if (!serviceIntegration) {
      return null
    }

    if (typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string') {
      return null
    }

    return (
      serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.additionalInformation?.update ?? (null as UpdateAdditionalInformation | null)
    )
  }, [serviceIntegration])

  const _getDescription = (originalDescription: string) => {
    const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
    const accountConfigMappingSession = serviceIntegration
      ? typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
        ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
        : (serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.objectName ?? 'note')
      : DEFAUlT_ACCOUNT_CONFIG_MAPPING_SESSION

    if (accountConfigMappingSession === CRM_SESSION_OBJECT[serviceId]) {
      return originalDescription
    }

    return `<div>${meetingInfoHTML} ${originalDescription}</div>`
  }
  const handleSave = tracked(
    async (data: FormValues) => {
      const description = _getDescription(data.meetingDescription)
      try {
        await runMutationWithConfirmation(async () => {
          const response = await triggerSaveMeeting({
            accountId,
            connector: serviceId,
            body: {
              roomId: roomSession.roomId,
              sessionId: roomSession.id,
              description: description,
              externalUserIds: [data.externalUserId],
              externalMeetingId: data.externalMeetingId,
              ...(data.notifyUserId ? { notifyUserId: data.notifyUserId } : {}),
              ...(data.notifyTeamId ? { notifyTeamId: data.notifyTeamId } : {}),
              relatedTo: {
                id: data.externalUserId,
                type: contactObject,
              },
              exportType: 'export',
            },
          })
          if (!response.error) {
            const { data: newRoomSession } = await triggerGetRoomSession({
              accountId,
              roomId: roomSession.roomId,
              sessionId: roomSession.id,
            })
            if (newRoomSession?.data) {
              dispatch(setRoomSession(newRoomSession.data))
              setSavedMeetingResult(true)
            }
          }
        })
      } catch (e) {
        console.error(e)
      }
    },
    { eventName: TrackEvent.SAVE_MEETING_TO_CRM },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return {
          roomId: roomSession.roomId,
          sessionId: roomSession.id,
          externalUserId: data.externalUserId,
          connector: serviceId,
        }
      },
    },
  )
  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit } = form

  const handleContactSelected = (contact?: User) => {
    if (!contact || !contact.metadata) {
      return
    }
  }

  const handleOnHide = useCallback(() => {
    onHide()
    form.reset()
  }, [onHide, form])

  useEffect(() => {
    if (savedMeetingResult && !roomSessionSyncStatus.length) {
      dispatch(
        addToast({
          variant: 'success',
          title: 'Success',
          messages: [`The meeting has been saved to ${serviceId}.`],
        }),
      )
      handleOnHide()
    }
  }, [savedMeetingResult, roomSessionSyncStatus, handleOnHide, serviceId, dispatch])

  return (
    <>
      <Modal
        size='xl'
        show={show}
        onHide={onHide}
        header={
          !savedMeetingResult ? (
            <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
              Save your meeting to&nbsp;<span className='text-capitalize'>{serviceId}</span>
            </BootstrapModal.Title>
          ) : null
        }
        body={
          savedMeetingResult ? (
            <IntegrationSavedModalBody
              syncStatuses={roomSessionSyncStatus.length ? [roomSessionSyncStatus[0]] : []}
              serviceId={serviceId}
              onHide={handleOnHide}
            />
          ) : (
            <FormProvider {...form}>
              <AddMeetingToCRMModalBody
                roomSession={roomSession}
                serviceId={serviceId}
                meetingTemplate={meetingTemplate}
                isEventAppendSupported={isEventAppendSupported}
                isNotifySupported={isNotifySupported}
                sessionObject={sessionMapping}
                sessionLabel={sessionLabel}
                updateInformation={updateInformation ?? undefined}
                onContactSelected={handleContactSelected}
              />
            </FormProvider>
          )
        }
        showFooterActions={!savedMeetingResult}
        onFooterPrimaryAction={handleSubmit(handleSave)}
        footerPrimaryActionLoading={isLoading || accountIsLoading || isGetRoomSessionLoading}
        onFooterSecondaryAction={onHide}
        centered={true}
      />
      <ConfirmationModal />
    </>
  )
}

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

interface AddMeetingToCRMModalBodyProps {
  roomSession: RoomSession
  meetingTemplate?: MeetingTemplate
  serviceId: IntegrationServiceId
  isEventAppendSupported: boolean
  isNotifySupported: boolean
  sessionObject: string
  sessionLabel: string
  onContactSelected?: (contact?: User) => void
  updateInformation?: UpdateAdditionalInformation
}

const AddMeetingToCRMModalBody: React.FC<AddMeetingToCRMModalBodyProps> = ({
  roomSession,
  meetingTemplate,
  serviceId,
  isEventAppendSupported,
  isNotifySupported,
  sessionObject,
  sessionLabel,
  updateInformation,
  onContactSelected,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { control, setValue, watch } = useFormContext<FormValues>()
  const { trackingClient } = useTracking()
  const [cookies, setCookie] = useCookies(['defaultCRMExportPresets'])

  const aiFeatures = useAppSelector(selectAiFeatures)
  const _sessionMeetingTemplate = useAppSelector(selectSessionMeetingTemplate)
  const groupedTasks = useAppSelector(selectGroupedTasks)

  const selectedExternalUserId = watch('externalUserId')

  const [collapsed, setCollapsed] = useState<boolean>(false)
  const [selectedPresets, setSelectedPresets] = useState<AiFeaturePreset[]>([])
  const [isModified, setIsModified] = useState<boolean>(false)

  const editorRef = useRef<LexicalEditor>(null)
  const hasPopulatedEditor = useRef(false)
  const isInitializedTracking = useRef(false)

  const activeMeetingTemplate = useMemo(() => {
    return meetingTemplate || _sessionMeetingTemplate || null
  }, [meetingTemplate, _sessionMeetingTemplate])

  const { data: roomData } = useGetRoomQuery(
    {
      accountId,
      roomId: roomSession.roomId,
    },
    { skip: !accountId || !roomSession.roomId },
  )

  const templatePresetIds = useMemo(() => activeMeetingTemplate?.presets.map((p) => p.aiFeaturePresetId) ?? [], [activeMeetingTemplate])

  const { data: presetsData } = useGetPresetsQuery(
    {
      accountId,
      ids: templatePresetIds,
      pageRequest: { page: 0, size: 500 },
    },
    { skip: !accountId || templatePresetIds.length === 0 },
  )

  const aiFeaturePresets = useMemo(() => {
    if (!activeMeetingTemplate || !presetsData?.data) {
      return []
    }

    const orderedPresets = getOrderedMeetingTemplatePresets(activeMeetingTemplate.presets)
    return orderedPresets.map((op) => presetsData.data.find((p) => p.id === op.aiFeaturePresetId)).filter((preset): preset is AiFeaturePreset => !!preset)
  }, [activeMeetingTemplate, presetsData])

  const aiFeaturePresetIds = useMemo(() => aiFeaturePresets.map((p) => p.id), [aiFeaturePresets])

  const { data: inputParametersHierarchyNodes } = useGetInputParametersHierarchyNodeByPresetsQuery(
    {
      accountId,
      type: 'ai-feature-presets',
      typeIds: aiFeaturePresetIds,
      effective: true,
      meetingTemplateId: activeMeetingTemplate?.id,
      userId: roomData?.createdBy.type === 'user' ? roomData.createdBy.id : undefined,
    },
    {
      skip: !accountId || !activeMeetingTemplate || aiFeaturePresetIds.length === 0 || !roomData,
    },
  )

  const { data: aiResults, isFetching: isAIResultsFetching } = useListAiResultsQuery(
    {
      accountId,
      roomId: roomSession.roomId,
      sessionId: roomSession.id,
      pageRequest: { page: 0, size: 500 },
    },
    { refetchOnMountOrArgChange: true },
  )

  const [searchTerm, setSearchTerm] = useState<string | null>(null)
  const {
    data: contactsData,
    isError: isContactsError,
    error: contactsError,
    isFetching: isContactsFetching,
  } = useGetIntegrationUsersQuery(
    {
      accountId,
      connector: serviceId,
      filters: searchTerm ? { name: searchTerm } : undefined,
    },
    { skip: !accountId || !serviceId },
  )

  const contacts = useMemo(() => contactsData?.data || [], [contactsData])
  const contactOptions = useMemo(() => getContactOptions(contacts, serviceId), [contacts, serviceId])

  const { data: additionalObjects, isFetching: isAdditionalObjectsFetching } = useGetAdditionalObjectsQuery(
    {
      accountId,
      connector: serviceId,
      relatedTo: { id: selectedExternalUserId },
      meetingStartDate: new Date(new Date(roomSession.startedAt).getTime() - EVENT_DATE_OFFSET).toISOString(),
      meetingEndDate: new Date(new Date(roomSession.finishedAt ?? roomSession.startedAt).getTime() + EVENT_DATE_OFFSET).toISOString(),
    },
    { skip: !selectedExternalUserId || !isEventAppendSupported, refetchOnMountOrArgChange: true },
  )

  const eventOptions = useMemo(() => {
    if (!additionalObjects) {
      return []
    }
    const config = updateInformation?.title
      ? {
          path: sessionObject,
          idKey: updateInformation.idKey ?? eventConfigMap[serviceId]?.idKey ?? 'Id',
          labelKey: updateInformation.title,
          household: { householdPath: 'household', eventPath: sessionObject },
        }
      : eventConfigMap[serviceId]
    return config ? extractEvents(additionalObjects, config) : []
  }, [additionalObjects, serviceId, sessionObject, updateInformation])

  const sessionMetadata = useMemo(() => parseMetadata(roomSession.metadata), [roomSession.metadata])
  const { source, provider = null, external } = sessionMetadata
  const effectiveSource = useEffectiveSource(source, external?.platform, external?.userAgent, provider)

  const onSearchInputChange = useCallback(async (input: string) => {
    setSearchTerm(input)
  }, [])

  const onSelectPreset = useCallback((preset: AiFeaturePreset) => {
    setSelectedPresets((prev) => {
      const exists = prev.some((p) => p.id === preset.id)
      return exists ? prev.filter((p) => p.id !== preset.id) : [...prev, preset]
    })
  }, [])

  const handleOnSelectAll = useCallback(() => {
    setSelectedPresets([...aiFeaturePresets])
  }, [aiFeaturePresets])

  const handleOnCancel = useCallback(() => {
    setSelectedPresets([])
  }, [])

  const handleTextContentChange = useCallback(() => {
    if (!isModified && isInitializedTracking.current) {
      setIsModified(true)
      trackingClient.track({
        eventName: TrackEvent.MEETING_EXPORT_EDITED,
        eventDetails: { serviceId },
      })
    }
    isInitializedTracking.current = true
  }, [isModified, serviceId, trackingClient])

  const handleOnChange = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      editorState.read(() => {
        const htmlValue = $generateHtmlFromNodes(editor, null)
        setValue('meetingDescription', htmlValue)
      })
    },
    [setValue],
  )

  const handleContactSelected = useCallback(
    (option: SelectOption) => {
      setValue('externalMeetingId', null)
      if (onContactSelected) {
        const selectedContact = contacts.find((contact) => {
          const contactIntegration = contact.integrations?.find((i) => i.serviceId === serviceId)
          return contactIntegration?.externalId === option.id
        })
        onContactSelected(selectedContact)
      }
    },
    [contacts, onContactSelected, serviceId, setValue],
  )

  const addPresetsToEditor = useCallback(
    (presetsToAdd: AiFeaturePreset[]) => {
      if (!presetsToAdd.length || !inputParametersHierarchyNodes || !aiResults?.data) {
        return
      }

      const activePresetsIds = presetsToAdd.map((preset) => preset.id)
      setCookie('defaultCRMExportPresets', activePresetsIds, { path: '/', maxAge: COOKIE_MAX_AGE })

      const addText: string[] = []

      for (const preset of presetsToAdd) {
        const presetAiFeature = aiFeatures.find((f) => f.preset?.id === preset.id)
        if (presetAiFeature) {
          const presetResults = getPresetResultsByInputParameterHierarchy(preset.id, presetAiFeature.id, aiResults.data, inputParametersHierarchyNodes).filter(
            (r) => r.status !== 'no_result',
          )

          if (presetResults.length) {
            addText.push(`<h2><u>${escapeHtml(presetAiFeature?.name ?? '')}</u></h2>`)
            addText.push(copyResult(presetAiFeature, presetResults[0], { groupedTasks })[HTML_TYPE])
          }
        }
      }

      const editor = editorRef.current
      if (editor && addText.length > 0) {
        editor.update(() => {
          const editorState = editor.getEditorState()
          const currentText = getEditorTextContent(editorState)
          const editorIsEmpty = !currentText.trim().length

          const parser = new DOMParser()
          const htmlString = addText.join('<br />')
          const dom = parser.parseFromString(htmlString, 'text/html')
          const nodes = $generateNodesFromDOM(editor, dom)

          $getRoot().select(editorIsEmpty ? 0 : undefined)
          $insertNodes(nodes)

          // Sync with form
          setValue('meetingDescription', htmlString)
        })
      }
    },
    [aiFeatures, inputParametersHierarchyNodes, aiResults, groupedTasks, setValue, setCookie],
  )

  const handleUserClickAdd = useCallback(() => {
    addPresetsToEditor(selectedPresets)
    setSelectedPresets([])
  }, [addPresetsToEditor, selectedPresets])

  useEffect(() => {
    if (hasPopulatedEditor.current) {
      return
    }

    const isDataReady = aiFeaturePresets.length > 0 && !!inputParametersHierarchyNodes && !!editorRef.current && !!aiResults?.data

    if (isDataReady) {
      const previouslySelectedIds = cookies.defaultCRMExportPresets
      const envPresets = PRESELECTED_PRESETS[env] || []

      const presetsToAdd =
        previouslySelectedIds && previouslySelectedIds.length > 0
          ? aiFeaturePresets.filter((p) => previouslySelectedIds.includes(p.id))
          : aiFeaturePresets.filter((p) => envPresets.includes(p.id))

      if (presetsToAdd.length > 0) {
        addPresetsToEditor(presetsToAdd)

        hasPopulatedEditor.current = true
      }
    }
  }, [aiFeaturePresets, inputParametersHierarchyNodes, cookies.defaultCRMExportPresets, addPresetsToEditor, aiResults])

  if (isAIResultsFetching && !aiResults) {
    return (
      <div className='d-flex justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (isContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((msg) => JSON.stringify(contactsError).match(msg))) {
    return (
      <Container className='p-0'>
        <Row>
          <Col>Your integration is invalid please reconnect your integration.</Col>
        </Row>
      </Container>
    )
  }
  return (
    <Container>
      <Row>
        <Col xs={12} className='crm-meeting-info-header'>
          <Container fluid className='p-0 d-flex align-items-center truncate-long-text'>
            <Row>
              <Col xs='auto'>
                <SourceIcon source={effectiveSource || source} />
              </Col>
              <Col className='d-flex flex-column justify-content-center overflow-hidden'>
                <Row>
                  <Col>
                    <div className='lead-medium'>
                      {formatDateHM(roomSession.startedAt)}-{roomSession.finishedAt ? formatDateHM(roomSession.finishedAt) : 'Live'}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <h6>{external?.title ? external.title : <div className='paragraph-extra-small'>Untitled meeting.</div>}</h6>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>

      <Row className='mt-3'>
        <Col xs={12}>
          <ContactDropdown
            fieldName='externalUserId'
            contactOptions={contactOptions}
            serviceId={serviceId}
            onSearchInputChange={onSearchInputChange}
            onContactOptionSelected={handleContactSelected}
            isLoading={isContactsFetching}
          />
        </Col>
      </Row>

      {isNotifySupported && (
        <div>
          <Row className='mt-3'>
            <Col xs={12}>
              <div className='lead-extra-small'>Notify user</div>
              <Controller
                name='notifyUserId'
                control={control}
                render={({ field }) => <UserSelectDropdown serviceId={serviceId} onOptionSelected={field.onChange} />}
              />
            </Col>
          </Row>
          <Row className='mt-3'>
            <Col xs={12}>
              <div className='lead-extra-small'>Notify team</div>
              <Controller
                name='notifyTeamId'
                control={control}
                render={({ field }) => <TeamSelectDropdown serviceId={serviceId} onOptionSelected={field.onChange} />}
              />
            </Col>
          </Row>
        </div>
      )}

      {isEventAppendSupported && (
        <Row className='mt-3'>
          <Col xs={12}>
            <div className='lead-extra-small'>{sessionLabel} (Optional)</div>
            <Controller
              name='externalMeetingId'
              control={control}
              render={({ field }) => {
                const option = field.value ? eventOptions.find((e) => e.id === field.value) : null
                return (
                  <SelectDropdown
                    initialSelectedOption={option ?? null}
                    options={eventOptions}
                    className='py-2 zocks-px-0.75'
                    onOptionSelected={(option) => field.onChange(option.id)}
                    onDeselect={() => field.onChange(null)}
                    placeholder={`Select your ${sessionLabel}`}
                    deselectOptionLabel={`Select your ${sessionLabel}`}
                    loading={isAdditionalObjectsFetching}
                    showDeselectOption={true}
                  />
                )
              }}
            />
          </Col>
        </Row>
      )}

      <Row className='mt-3'>
        <Col>
          <div className='lead-extra-small'>Meeting description</div>
        </Col>
      </Row>

      <Row className='mt-1 gy-3 zocks-bg-color-gray-100 zocks-p-0.75'>
        <Col xs={12} className='pt-0'>
          <div role='button' className='d-flex gap-2' onClick={() => setCollapsed((prev) => !prev)}>
            <div>Add your results to description</div>
            <i
              className={classNames('bi', {
                'bi-chevron-contract': collapsed,
                'bi-chevron-expand': !collapsed,
              })}
            />
          </div>
          <Collapse in={!collapsed} className='pt-3'>
            <div>
              <div className='d-flex flex-column gap-2'>
                <div className='d-flex gap-2 flex-wrap'>
                  {aiFeaturePresets.map((preset) => (
                    <SelectablePresetTag key={preset.id} preset={preset} selectedPresets={selectedPresets} onSelect={onSelectPreset} />
                  ))}
                  <Button variant='link' className='button-small' onClick={handleOnSelectAll}>
                    Select all
                  </Button>
                </div>
                <div className='d-flex gap-3 justify-content-end'>
                  <Button className='button-small' variant='secondary' onClick={handleOnCancel} disabled={!selectedPresets.length}>
                    Cancel
                  </Button>
                  <Button disabled={!selectedPresets.length} className='button-small' variant='primary' onClick={() => handleUserClickAdd()}>
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </Collapse>
        </Col>
        <Col>
          <Controller
            name='meetingDescription'
            control={control}
            render={() => (
              <WritableLexicalEditor
                ref={editorRef}
                onChange={handleOnChange}
                onTextContentChange={handleTextContentChange}
                className='flex-column-reverse align-items-start'
                editorContainerClassName='flex-grow-1 add-meeting-to-crm-editor-container'
                containerClassName='w-100'
                lexicalNodes={LEXICAL_NODES}
                header={
                  <RichTextToolbarPlugin className='px-3 zocks-py-0.75 gap-2'>
                    <div className='editable-text-result-rich-text-divider' />
                  </RichTextToolbarPlugin>
                }
              />
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}

interface SelectablePresetTagProps {
  preset: AiFeaturePreset
  selectedPresets: AiFeaturePreset[]
  onSelect: (preset: AiFeaturePreset) => void
}

const SelectablePresetTag: React.FC<SelectablePresetTagProps> = ({ preset, selectedPresets, onSelect }) => {
  const selectedOrder = selectedPresets.map((p) => p.id).findIndex((pid) => pid === preset.id)

  return (
    <div
      role='button'
      className={classNames('d-flex gap-2 align-items-center justify-content-center crm-selectable-preset-tag-container', {
        'crm-selectable-preset-tag-container__selected': selectedOrder >= 0,
      })}
      onClick={() => onSelect(preset)}
    >
      <div>{preset.name}</div>
      <div
        className={classNames('crm-selectable-preset-tag-order-container label-small', {
          'crm-selectable-preset-tag-order-container__selected': selectedOrder >= 0,
        })}
      >
        {selectedOrder >= 0 ? selectedOrder + 1 : null}
      </div>
    </div>
  )
}
